import { cn } from "@simpro/ui";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import type { PublicImage } from "../schemas/home.schemas";

export interface Testimonial {
  quote: string;
  name: string;
  role: string | null;
  rating: number;
  image: PublicImage | null;
}

export interface TestimonialsSectionProps {
  heading: string | null;
  items: readonly Testimonial[];
}

/** The board's four pebbles, each with its own fill and its own pinched corner, and a step down for each. */
const PEBBLES = [
  { shape: "pebble-1", fill: "bg-muted-bg", drop: "lg:mt-0" },
  { shape: "pebble-2", fill: "bg-tint", drop: "lg:mt-12" },
  { shape: "pebble-3", fill: "bg-raised", drop: "lg:mt-4" },
  { shape: "pebble-4", fill: "bg-surface", drop: "lg:mt-16" },
] as const;

/**
 * What customers said, in the salon's words (design note salon-home-landing.md 9.2; TDD-SA
 * FR-SA-194). Nothing until the salon has written one.
 *
 * Drawn as the Organic Wellness board's "Kind words" (design note salon-booking-organic-wellness.md
 * section 6 item 8): every quote at once on its own pebble, pale sage, blush, cream or sand in turn,
 * each with one pinched corner and set a little lower than the one before, so the row reads as laid by
 * hand. The quote is in the display face under a large opening mark in the accent; a pebble tilts and
 * lifts a little under the pointer. The rating is said, not drawn.
 */
export function TestimonialsSection({ heading, items }: TestimonialsSectionProps) {
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="testimonials-heading" className={cn(page, "flex flex-col gap-12")}>
      <SectionHeading id="testimonials-heading" title={heading ?? "Kind words"} />
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,17.5rem),1fr))] items-start gap-5">
        {items.map((item, index) => {
          const pebble = PEBBLES[index % PEBBLES.length] ?? PEBBLES[0];
          return (
            <Reveal as="li" key={`${item.name}-${index}`} index={index} className={pebble.drop}>
              <figure
                className={cn(
                  "flex flex-col gap-4 p-7 text-heading transition-transform duration-500 ease-[cubic-bezier(0.3,1.2,0.5,1)] hover:-translate-y-1 hover:-rotate-1 motion-reduce:transition-none",
                  pebble.shape,
                  pebble.fill,
                )}
              >
                <span aria-hidden="true" className="headline h-6 text-[4rem] leading-[0.75] text-highlight">
                  “
                </span>
                <blockquote className="headline text-[1.5rem] leading-[1.35]">{item.quote}</blockquote>
                <figcaption className="flex items-center gap-3 text-sm">
                  {item.image ? (
                    <img src={item.image.url} alt="" className="size-10 shrink-0 rounded-full object-cover" />
                  ) : null}
                  <span>
                    <span className="font-semibold">{item.name}</span>
                    {item.role ? <span className="text-subtle"> — {item.role}</span> : null}
                    <span className="sr-only">. Rated {item.rating} out of 5</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          );
        })}
      </ul>
    </section>
  );
}
